'use client';

import type React from 'react';

import { cn } from '@/lib/utils';
import { forwardRef, useState, useEffect } from 'react';

const Textarea = forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<'textarea'> & {
    showCounter?: boolean;
  }
>(({ className, maxLength, showCounter = true, onChange, ...props }, ref) => {
  const [currentLength, setCurrentLength] = useState(0);

  useEffect(() => {
    if (props.value !== undefined) {
      setCurrentLength(String(props.value).length);
    } else if (props.defaultValue !== undefined) {
      setCurrentLength(String(props.defaultValue).length);
    }
  }, [props.value, props.defaultValue]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentLength(e.target.value.length);
    if (onChange) {
      onChange(e);
    }
  };

  const shouldShowCounter = maxLength && showCounter;

  return (
    <div className="relative">
      <textarea
        className={cn(
          'flex min-h-[80px] w-full rounded-md border border-neutrals-N200 bg-white px-3 py-2 text-base ring-offset-white placeholder:text-neutrals-N700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutrals-N1000 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:border-neutrals-N800 dark:bg-neutrals-N1000 dark:ring-offset-neutrals-N1000 dark:placeholder:text-neutrals-N500 dark:focus-visible:ring-neutrals-N400',
          shouldShowCounter && 'pb-8',
          className,
        )}
        ref={ref}
        maxLength={maxLength}
        onChange={handleChange}
        {...props}
      />
      {shouldShowCounter && (
        <div className="absolute bottom-2 right-3 text-sm text-neutrals-N700 bg-white px-1 dark:text-neutrals-N500 dark:bg-neutrals-N1000">
          {currentLength}/{maxLength}
        </div>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

export { Textarea };
